import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AxiosError, AxiosResponse } from "axios";

import {
  TotalBalancesReponse,
  type ChangeBalanceRequest,
  type ConvertBalancesRequest,
  type UserBalanceResponse,
} from "./api.dto";
import { useApi } from "@/shared/api";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import { ApiResponse } from "@/shared/types";
import { useSessionStore } from "@/entities/session";
import { Roles } from "@/entities/user";

export const useBalance = ({
  userId,
  enabled,
}: {
  userId: string;
  enabled?: boolean;
}) => {
  const { api } = useApi();

  return useQuery<UserBalanceResponse>({
    queryKey: ["balance", userId],
    queryFn: async () => {
      const { data } = await api.get(`/balances/user/${userId}`);

      return data;
    },
    enabled,
  });
};

export const useChangeBalance = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Баланс успешно изменен.",
      },
    },
  });
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, ChangeBalanceRequest, Response>(
    {
      mutationKey: ["unbanUser"],
      mutationFn: ({ id, balance, totalIncome }) =>
        api.patch(`/balances/${id}`, { totalIncome, balance }),
      onSuccess,
      onError,
    },
  );
};

export const useConvertBalances = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["convertBalances"],
    mutationFn: async (data: ConvertBalancesRequest) =>
      await api.post("/balances/convert", data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["balance"] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });
};

export const useTotalBalances = () => {
  const { api } = useApi({ version: "v2" });
  const { user } = useSessionStore();
  const role = user?.role!;

  return useQuery({
    queryKey: ["totalBalances"],
    queryFn: async () => {
      const response =
        await api.get<ApiResponse<TotalBalancesReponse>>("/total-balances");

      return response.data.data.total;
    },
    enabled: role === Roles.Head,
  });
};
